import React, { useMemo } from 'react';

import {
  ClickAwayListener,
  IconButton,
  makeStyles,
  Popper,
  Fade
} from '@material-ui/core';

import { clsx, colorSet } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import { ChevronRight, Close } from '@carbon/icons-react';

import { NOT_FOUND_PURCHASE_ORDER } from '../assets';

import useDocuments from '../../../../hooks/useDocuments';
import { useSelector } from '@store';

import {
  documentDateFormat,
  formatCurrency,
  formatDate
} from '@utils/formatter';
import { ToolkitOutlineIcon } from '@assets';
import { getHistoryStatusLabel } from '../PurchaseOrder/utils';

interface ItemProps {
  id: string;
  createdAt: string;
  quotationPrice: number;
  dueDate: string;
  isLatest: boolean;
  isRejected?: boolean;
  onClick: (id: string) => void;
  statusLabel: ReturnType<typeof getHistoryStatusLabel>;
}

interface Props {
  title: string;
  items: ItemProps[];
  onClick: (id: string) => void;
}

const PurchaseOrderDropdown = ({ title, items, onClick }: Props) => {
  const classes = useStyles();

  const { onUpdateDocumentsOptions } = useDocuments();

  const anchorId = useSelector(
    (state) => state?.documents?.options?.listAnchorId
  );

  const anchorEl = useMemo(
    () => (anchorId ? document.getElementById(anchorId) : null),
    [anchorId]
  );

  const open = Boolean(anchorEl);

  const onClose = () => {
    onUpdateDocumentsOptions({ listAnchorId: null });
  };

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Popper
        className={classes.root}
        open={open}
        anchorEl={anchorEl}
        placement={'bottom-end'}
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={150}>
            <div className={classes.paper}>
              <div className={classes.header}>
                <CustomizedTypography
                  variant="body1"
                  fontWeight="500"
                  color="textPrimary"
                >
                  {title}
                </CustomizedTypography>

                <IconButton className="close" onClick={onClose}>
                  <Close />
                </IconButton>
              </div>

              <div
                className={clsx(classes.content, {
                  notfound: items.length === 0
                })}
              >
                {items.length === 0 && (
                  <>
                    <img src={NOT_FOUND_PURCHASE_ORDER} alt="" />
                    <CustomizedTypography variant="body1" color="textSecondary">
                      아직 발주서를 발행하지 않았습니다.
                    </CustomizedTypography>
                  </>
                )}

                {items.map((item, index) => (
                  <DropdownItem key={index} {...item} onClick={onClick} />
                ))}
              </div>
            </div>
          </Fade>
        )}
      </Popper>
    </ClickAwayListener>
  );
};

export default PurchaseOrderDropdown;

const DropdownItem = ({
  id,
  createdAt,
  quotationPrice,
  dueDate,
  statusLabel,
  onClick
}: ItemProps) => {
  const classes = useStyles();

  return (
    <div className={classes.item} onClick={() => onClick && onClick(id)}>
      <img
        width={44}
        height={44}
        src={ToolkitOutlineIcon}
        alt="ToolkitOutlineIcon"
      />

      <div
        className={clsx(classes.chip, {
          'stopped': statusLabel === '진행중단',
          'done': statusLabel === '거래완료',
          'processing': statusLabel === '거래 진행중'
        })}
      >
        {statusLabel}
      </div>

      <div className={classes.itemContent}>
        <CustomizedTypography
          variant="body1"
          color="textPrimary"
          fontWeight="500"
          gutterBottoms={2}
        >
          {documentDateFormat(createdAt)} 발주일
        </CustomizedTypography>
        <CustomizedTypography
          variant="body2"
          color="textSecondary"
          gutterBottoms={2}
        >
          결제금액: {formatCurrency(quotationPrice)}원
        </CustomizedTypography>
        <CustomizedTypography
          variant="body2"
          color="textSecondary"
          gutterBottoms={12}
        >
          납기일: {formatDate(dueDate, 'YYYY.MM.DD') + '까지'}
        </CustomizedTypography>
        <CustomizedTypography
          variant="body2"
          color="textPrimary"
          fontWeight="500"
          className="itemContentButton"
          style={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          발주서확인 <ChevronRight width={20} height={20} />
        </CustomizedTypography>
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: 16,
    maxWidth: 375,

    width: '100%',
    zIndex: 1300
  },
  paper: {
    borderRadius: 16,
    width: '100%',
    backgroundColor: colorSet.gray000,
    boxShadow:
      '0px 5px 5px -3px rgba(0, 0, 0, 0.1), 0px 8px 10px 1px rgba(0, 0, 0, 0.07), 0px 3px 14px 2px rgba(0, 0, 0, 0.06)'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    position: 'relative',
    borderBottom: `1px solid ${colorSet.gray400}`,
    '& .close': {
      position: 'absolute',
      right: 12,
      padding: 0,
      '& svg': {
        width: 24,
        height: 24,
        color: colorSet.gray1000
      }
    }
  },
  content: {
    height: '100%',
    maxHeight: 520,
    overflowY: 'scroll',
    '&.notfound': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      minHeight: 545,
      '& img': {
        marginBottom: 32
      }
    }
  },
  item: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '20px 16px',
    cursor: 'pointer',
    position: 'relative',
    '&:hover': {
      backgroundColor: colorSet.gray100
    }
  },
  itemContent: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    marginLeft: 12
  },
  chip: {
    padding: '4px 8px',
    borderRadius: 100,
    position: 'absolute',
    top: 20,
    right: 16,
    backgroundColor: colorSet.primary200,
    color: colorSet.primary500,
    ...theme.typography.caption,
    '&.stopped': {
      backgroundColor: colorSet.errorLight,
      color: colorSet.errorMain
    },
    '&.processing': {
      backgroundColor: colorSet.primary200,
      color: colorSet.primary500
    },
    '&.done': {
      backgroundColor: colorSet.primary500,
      color: colorSet.gray000
    }
  }
}));
